import React, { useState } from 'react';
import { Copy, Check, Terminal, User, Server } from 'lucide-react';
export function HowItWorks() {
  const [activeTab, setActiveTab] = useState<'jwt' | 'master'>('jwt');
  const jwtCode = `// Method A: Client-side with JWT
// Use your user's JWT token (Clerk, PropelAuth, etc)
const openai = new OpenAI({
  baseURL: 'https://api.tokengates.sh/v1/proj_123',
  apiKey: 'user_jwt_token' 
})

const response = await openai.chat.completions.create({
  model: 'gpt-4',
  messages: [{ role: 'user', content: 'Hello' }]
})`;
  const masterCode = `// Method B: Backend with Master Key
// Attribute usage via customer_id or tenant_id
const openai = new OpenAI({
  baseURL: 'https://api.tokengates.sh/v1/proj_123',
  apiKey: 'master_api_key',
  defaultHeaders: {
    'x-customer-id': 'user_123'
  }
})

const response = await openai.chat.completions.create({
  model: 'gpt-4',
  messages: [{ role: 'user', content: 'Hello' }]
})`;
  return <section id="how-it-works" className="py-24 bg-white" data-id="element-157">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-158">
        <div className="text-center mb-20" data-id="element-159">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-160">
            Stupidly Simple. 5 Minutes to Launch.
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-161">
            Two ways to authenticate your customers and attribute usage
            automatically.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-162">
          <div className="space-y-12" data-id="element-163">
            <div className="flex gap-6" data-id="element-164">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shrink-0" data-id="element-165">
                1
              </div>
              <div data-id="element-166">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-167">
                  Create Project
                </h3>
                <p className="text-gray-600" data-id="element-168">
                  Create a project in TokenGates.sh and name it. You'll get a
                  unique endpoint immediately.
                </p>
              </div>
            </div>
            <div className="flex gap-6" data-id="element-169">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shrink-0" data-id="element-170">
                2
              </div>
              <div data-id="element-171">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-172">
                  Connect Your Stack
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-173">
                  Connect your Auth API (Clerk, PropelAuth), LLM providers
                  (OpenAI, Anthropic), and your Billing provider (Stripe,
                  Polar.sh).
                </p>
              </div>
            </div>
            <div className="flex gap-6" data-id="element-174">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shrink-0" data-id="element-175">
                3
              </div>
              <div data-id="element-176">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-177">
                  Send Requests
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-178">
                  Use your existing auth provider's JWT tokens to authenticate
                  customers directly, or use a Master API Key for
                  backend-to-backend requests.
                </p>
                <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg border border-gray-200 font-mono text-sm text-blue-600" data-id="element-179">
                  <span data-id="element-180">https://api.tokengates.sh/v1/proj_123</span>
                  <Copy className="w-4 h-4 cursor-pointer hover:text-blue-800" data-id="element-181" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl" data-id="element-182">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700" data-id="element-183">
              <div className="flex gap-4" data-id="element-184">
                <button onClick={() => setActiveTab('jwt')} className={`text-xs font-bold uppercase tracking-wider pb-1 border-b-2 transition-colors ${activeTab === 'jwt' ? 'text-blue-400 border-blue-400' : 'text-gray-500 border-transparent hover:text-gray-300'}`} data-id="element-185">
                  JWT (Client)
                </button>
                <button onClick={() => setActiveTab('master')} className={`text-xs font-bold uppercase tracking-wider pb-1 border-b-2 transition-colors ${activeTab === 'master' ? 'text-blue-400 border-blue-400' : 'text-gray-500 border-transparent hover:text-gray-300'}`} data-id="element-186">
                  Master Key (Backend)
                </button>
              </div>
              <div className="text-xs text-gray-400 font-mono flex items-center gap-2" data-id="element-187">
                <Terminal className="w-3 h-3" data-id="element-188" />{' '}
                {activeTab === 'jwt' ? 'client.ts' : 'server.ts'}
              </div>
            </div>
            <div className="p-6 overflow-x-auto min-h-[280px]" data-id="element-189">
              <pre className="text-sm font-mono text-gray-300" data-id="element-190">
                <code data-id="element-191">{activeTab === 'jwt' ? jwtCode : masterCode}</code>
              </pre>
            </div>
            <div className="bg-blue-600/10 border-t border-blue-500/20 p-6" data-id="element-192">
              <div className="flex items-center gap-2 text-blue-400 text-sm font-bold mb-4 uppercase tracking-wider" data-id="element-193">
                {activeTab === 'jwt' ? <User className="w-4 h-4" data-id="element-194" /> : <Server className="w-4 h-4" data-id="element-195" />}
                <span data-id="element-196">
                  {activeTab === 'jwt' ? 'Direct Customer Auth' : 'Backend Attribution'}
                </span>
              </div>
              <p className="text-xs text-gray-400 mb-4 leading-relaxed" data-id="element-197">
                {activeTab === 'jwt' ? 'Authenticate users directly using tokens from Clerk, PropelAuth, or Supabase. We verify the JWT and attribute usage to the user automatically.' : "Use a Master API Key from your backend. Pass a customer_id or tenant_id header, and we'll handle the attribution and billing meters."}
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2" data-id="element-198">
                {['Tokens counted in real-time', 'Cost + Margin calculated', 'Per-user attribution tracked', 'Synced to billing meter', 'Cost limits enforced', 'Customer billed automatically'].map((item, i) => <li key={i} className="flex items-center gap-2 text-xs text-gray-300" data-id="element-199">
                    <Check className="w-3 h-3 text-green-500" data-id="element-200" /> {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-201">
          <div className="p-6" data-id="element-202">
            <div className="text-4xl font-bold text-blue-600 mb-2" data-id="element-203">0</div>
            <div className="text-gray-900 font-bold" data-id="element-204">Lines of backend code</div>
          </div>
          <div className="p-6" data-id="element-205">
            <div className="text-4xl font-bold text-blue-600 mb-2" data-id="element-206">0</div>
            <div className="text-gray-900 font-bold" data-id="element-207">
              Infrastructure to deploy
            </div>
          </div>
          <div className="p-6" data-id="element-208">
            <div className="text-4xl font-bold text-blue-600 mb-2" data-id="element-209">0</div>
            <div className="text-gray-900 font-bold" data-id="element-210">Ops work required</div>
          </div>
        </div>
      </div>
    </section>;
}